import { useForm } from "react-hook-form";
import { useState, useEffect } from "react";
import { Plus, X, Loader2, Ban, CheckCircle, Info, Lock } from "lucide-react";
import { useAuth } from "../../providers/AuthProvider";
import { useUsers, type User, type Role, type RoleAssignment } from "./UsersProvider";
import { useGroups, type PermissionGroup } from "./GroupsProvider";
import { LABEL_CONFIG } from "../../components/HierarchyPicker/types";
import { HierarchyPicker } from "../../components/HierarchyPicker/HierarchyPicker";

const INDY_TENANT_ID = "indytv-platform";
const TENANT_ADMIN_ROLE_NAME = "tenant_admin";

// A role that grants the "*" wildcard is platform-level (e.g. super_admin /
// indy_admin) and may ONLY be assigned within the indytv-platform tenant.
// Gating on the permission rather than the role name keeps this correct
// regardless of what the wildcard role is called.
const isPlatformWildcardRole = (role: Role): boolean => role.permissions.includes("*");

const isRoleAllowedForGroup = (role: Role, group: PermissionGroup | undefined): boolean => {
  if (!group) return true;
  if (isPlatformWildcardRole(role)) return group.tenantId === INDY_TENANT_ID;
  if (role.name === TENANT_ADMIN_ROLE_NAME) return group.label === "tenant";
  return true;
};

type Props = {
  user?: User | null;
  defaultGroupId?: string | null;
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: UserFormData) => Promise<void>;
};

export type UserFormData = {
  email: string;
  firstName: string;
  lastName: string;
  password?: string;
  roleAssignments?: RoleAssignmentInput[];
  // For editing: track what to add and remove
  addRoleAssignments?: RoleAssignmentInput[];
  removeRoleAssignments?: { roleId: string; groupId: string }[];
};

export type RoleAssignmentInput = {
  roleId: string;
  groupId: string;
  includeDescendants: boolean;
};

// --- Role Assignment Row ---

type RoleAssignmentRowProps = {
  assignment: RoleAssignmentInput;
  index: number;
  roles: Role[];
  groups: PermissionGroup[];
  onChange: (index: number, field: keyof RoleAssignmentInput, value: string | boolean) => void;
  onRemove: (index: number) => void;
  canRemove: boolean;
  readOnly?: boolean;
};

const RoleAssignmentRow = ({
  assignment,
  index,
  roles,
  groups,
  onChange,
  onRemove,
  canRemove,
  readOnly,
}: RoleAssignmentRowProps) => {
  const selectedGroup = groups.find((g) => g.id === assignment.groupId);
  const availableRoles = roles.filter((r) => isRoleAllowedForGroup(r, selectedGroup));
  const selectedRole = roles.find((r) => r.id === assignment.roleId);

  return (
    <div className="p-3 bg-zinc-800/50 border border-zinc-700/60 rounded-lg space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-[11px] font-medium uppercase tracking-wide text-zinc-400 mb-1">Group</label>
          <HierarchyPicker
            groups={groups}
            selectedId={assignment.groupId || null}
            onChange={(id) => onChange(index, "groupId", id ?? "")}
            placeholder="Select group..."
            disabled={readOnly}
          />
        </div>
        <div>
          <label className="block text-[11px] font-medium uppercase tracking-wide text-zinc-400 mb-1">Role</label>
          <select
            value={assignment.roleId}
            onChange={(e) => onChange(index, "roleId", e.target.value)}
            disabled={readOnly || !assignment.groupId}
            className="w-full px-3 py-2 bg-zinc-900 border border-zinc-600 rounded text-sm text-white focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            <option value="">{assignment.groupId ? "Select role..." : "Pick a group first"}</option>
            {availableRoles.map((role) => (
              <option key={role.id} value={role.id}>
                {role.displayName}
              </option>
            ))}
          </select>
        </div>
      </div>

      {selectedRole?.description && <p className="text-xs text-zinc-400 leading-relaxed">{selectedRole.description}</p>}

      {canRemove && !readOnly && (
        <div className="flex justify-end">
          <button
            type="button"
            onClick={() => onRemove(index)}
            className="flex items-center gap-1 px-2 py-1 text-xs text-zinc-400 hover:text-red-400 transition-colors"
          >
            <X size={14} />
            Remove
          </button>
        </div>
      )}
    </div>
  );
};

const buildEffectivePermissionsSummary = (
  assignments: RoleAssignmentInput[],
  roles: Role[],
  groups: PermissionGroup[],
): { role: Role; group: PermissionGroup }[] => {
  return assignments
    .map((a) => {
      const role = roles.find((r) => r.id === a.roleId);
      const group = groups.find((g) => g.id === a.groupId);
      return role && group ? { role, group } : null;
    })
    .filter((x): x is { role: Role; group: PermissionGroup } => x !== null);
};

// --- User Form Dialog ---

export const UserFormDialog = ({ user, defaultGroupId, isOpen, onClose, onSubmit }: Props) => {
  const isEditing = !!user;
  const { user: currentUser } = useAuth();
  const isSelf = isEditing && currentUser?.id === user?.id;
  const { roles, getUserRoleAssignments, setUserDisabled } = useUsers();
  const { groups } = useGroups();
  const [disableLoading, setDisableLoading] = useState(false);
  const [disableError, setDisableError] = useState<string | null>(null);
  const [isDisabled, setIsDisabled] = useState(user?.disabled ?? false);

  const [resetPassword, setResetPassword] = useState(false);
  const [roleAssignments, setRoleAssignments] = useState<RoleAssignmentInput[]>([]);
  const [originalAssignments, setOriginalAssignments] = useState<RoleAssignment[]>([]);
  const [loadingAssignments, setLoadingAssignments] = useState(false);
  const [roleError, setRoleError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setError,
    reset,
  } = useForm<UserFormData>({
    defaultValues: {
      email: user?.email ?? "",
      firstName: user?.firstName ?? "",
      lastName: user?.lastName ?? "",
      password: "",
    },
  });

  // Reset form and fetch role assignments when dialog opens
  useEffect(() => {
    if (isOpen) {
      setRoleError(null);
      setDisableError(null);
      setResetPassword(false);
      setIsDisabled(user?.disabled ?? false);
      reset({
        email: user?.email ?? "",
        firstName: user?.firstName ?? "",
        lastName: user?.lastName ?? "",
        password: "",
      });

      if (user) {
        // Editing: fetch existing role assignments
        setLoadingAssignments(true);
        getUserRoleAssignments(user.id)
          .then((assignments) => {
            setOriginalAssignments(assignments);
            setRoleAssignments(
              assignments.map((a) => ({
                roleId: a.roleId,
                groupId: a.groupId,
                includeDescendants: a.includeDescendants,
              })),
            );
          })
          .catch((err) => {
            console.error("Failed to fetch role assignments:", err);
            setOriginalAssignments([]);
            setRoleAssignments([]);
          })
          .finally(() => setLoadingAssignments(false));
      } else {
        // New user: start with one assignment row, pre-fill group if provided
        setOriginalAssignments([]);
        setRoleAssignments([{ roleId: "", groupId: defaultGroupId ?? "", includeDescendants: true }]);
      }
    }
  }, [isOpen, user, reset, getUserRoleAssignments]);

  const addRoleAssignment = () => {
    setRoleAssignments([...roleAssignments, { roleId: "", groupId: "", includeDescendants: true }]);
  };

  const updateRoleAssignment = (index: number, field: keyof RoleAssignmentInput, value: string | boolean) => {
    setRoleAssignments((prev) =>
      prev.map((assignment, i) => (i === index ? { ...assignment, [field]: value } : assignment)),
    );
    setRoleError(null);
  };

  const removeRoleAssignment = (index: number) => {
    setRoleAssignments((prev) => prev.filter((_, i) => i !== index));
  };

  const handleFormSubmit = async (data: UserFormData) => {
    try {
      // Filter out incomplete role assignments
      const validAssignments = roleAssignments.filter((a) => a.roleId && a.groupId);

      // Require at least one role assignment when creating a user
      if (!isEditing && validAssignments.length === 0) {
        setRoleError("At least one role assignment is required");
        return;
      }
      setRoleError(null);

      if (isEditing) {
        const originalKeys = new Set(originalAssignments.map((a) => `${a.roleId}:${a.groupId}`));
        const currentKeys = new Set(validAssignments.map((a) => `${a.roleId}:${a.groupId}`));

        const toAdd = isSelf ? [] : validAssignments.filter((a) => !originalKeys.has(`${a.roleId}:${a.groupId}`));
        const toRemove = isSelf
          ? []
          : originalAssignments
              .filter((a) => !currentKeys.has(`${a.roleId}:${a.groupId}`))
              .map((a) => ({ roleId: a.roleId, groupId: a.groupId }));

        await onSubmit({
          email: data.email,
          firstName: data.firstName,
          lastName: data.lastName,
          // Only send a password when the admin opted in and entered one;
          // otherwise the existing password is left unchanged.
          password: resetPassword && data.password ? data.password : undefined,
          addRoleAssignments: toAdd,
          removeRoleAssignments: toRemove,
        });
      } else {
        await onSubmit({ ...data, roleAssignments: validAssignments });
      }

      reset();
      setRoleAssignments([]);
      setOriginalAssignments([]);
      setRoleError(null);
      onClose();
    } catch (err: any) {
      setError("root", { message: err.message });
    }
  };

  const handleClose = () => {
    reset();
    setRoleAssignments([]);
    setOriginalAssignments([]);
    setRoleError(null);
    setDisableError(null);
    onClose();
  };

  const handleToggleDisabled = async () => {
    if (!user) return;
    setDisableLoading(true);
    setDisableError(null);
    try {
      const updated = await setUserDisabled(user.id, !isDisabled);
      setIsDisabled(updated.disabled);
    } catch (err: any) {
      setDisableError(err.message);
    } finally {
      setDisableLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60" onClick={handleClose} />

      {/* Dialog */}
      <div className="relative bg-zinc-900 border border-zinc-700 rounded-lg shadow-xl w-full max-w-2xl p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-white">{isEditing ? "Edit User" : "Create User"}</h2>
          {isEditing && !isSelf && (
            <button
              type="button"
              onClick={handleToggleDisabled}
              disabled={disableLoading}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded transition-colors ${
                isDisabled
                  ? "text-green-400 bg-green-500/20 hover:bg-green-500/30"
                  : "text-red-400 bg-red-500/20 hover:bg-red-500/30"
              } disabled:opacity-50`}
            >
              {disableLoading ? (
                <Loader2 size={12} className="animate-spin" />
              ) : isDisabled ? (
                <CheckCircle size={12} />
              ) : (
                <Ban size={12} />
              )}
              {isDisabled ? "Enable User" : "Disable User"}
            </button>
          )}
        </div>

        {disableError && (
          <div className="mb-4 p-3 bg-red-500/20 border border-red-500/50 rounded text-red-300 text-sm">
            {disableError}
          </div>
        )}

        <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-4">
          {errors.root && (
            <div className="p-3 bg-red-500/20 border border-red-500/50 rounded text-red-300 text-sm">
              {errors.root.message}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-zinc-300 mb-1">Email</label>
            <input
              type="email"
              {...register("email", { required: "Email is required" })}
              className="w-full px-3 py-2 bg-zinc-800 border border-zinc-600 rounded text-white placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="user@example.com"
            />
            {errors.email && <p className="mt-1 text-sm text-red-400">{errors.email.message}</p>}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-zinc-300 mb-1">First Name</label>
              <input
                type="text"
                {...register("firstName", { required: "First name is required" })}
                className="w-full px-3 py-2 bg-zinc-800 border border-zinc-600 rounded text-white placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="John"
              />
              {errors.firstName && <p className="mt-1 text-sm text-red-400">{errors.firstName.message}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-zinc-300 mb-1">Last Name</label>
              <input
                type="text"
                {...register("lastName", { required: "Last name is required" })}
                className="w-full px-3 py-2 bg-zinc-800 border border-zinc-600 rounded text-white placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Doe"
              />
              {errors.lastName && <p className="mt-1 text-sm text-red-400">{errors.lastName.message}</p>}
            </div>
          </div>

          {!isEditing && (
            <div>
              <label className="block text-sm font-medium text-zinc-300 mb-1">Password</label>
              <input
                type="password"
                {...register("password", {
                  required: !isEditing && "Password is required",
                  minLength: { value: 8, message: "Password must be at least 8 characters" },
                })}
                className="w-full px-3 py-2 bg-zinc-800 border border-zinc-600 rounded text-white placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="••••••••"
              />
              {errors.password && <p className="mt-1 text-sm text-red-400">{errors.password.message}</p>}
            </div>
          )}

          {isEditing && (
            <div>
              <label className="flex items-center gap-2 text-sm font-medium text-zinc-300 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={resetPassword}
                  onChange={(e) => setResetPassword(e.target.checked)}
                  className="rounded border-zinc-600 bg-zinc-800 text-blue-600 focus:ring-blue-500"
                />
                Reset password
              </label>
              {resetPassword && (
                <div className="mt-2">
                  <input
                    type="password"
                    {...register("password", {
                      validate: (value) =>
                        !resetPassword || (value && value.length >= 8) || "Password must be at least 8 characters",
                    })}
                    className="w-full px-3 py-2 bg-zinc-800 border border-zinc-600 rounded text-white placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="New password (min 8 characters)"
                  />
                  {errors.password && <p className="mt-1 text-sm text-red-400">{errors.password.message}</p>}
                  <p className="mt-1 text-xs text-zinc-500">Leave unchecked to keep the current password.</p>
                </div>
              )}
            </div>
          )}

          {/* Role Assignments */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-zinc-300">Role Assignments</label>
              {!isSelf && (
                <button
                  type="button"
                  onClick={addRoleAssignment}
                  disabled={loadingAssignments}
                  className="flex items-center gap-1 px-2 py-1 text-xs text-blue-400 hover:text-blue-300 transition-colors disabled:opacity-50"
                >
                  <Plus size={14} />
                  Add Role
                </button>
              )}
            </div>

            {isSelf && (
              <div className="mb-2 flex items-start gap-2 p-3 bg-yellow-500/10 border border-yellow-500/30 rounded text-xs text-yellow-200">
                <Lock size={14} className="mt-0.5 flex-shrink-0" />
                <span>
                  You cannot edit your own role assignments. Ask another admin to change your access if needed.
                </span>
              </div>
            )}

            {loadingAssignments ? (
              <div className="flex items-center gap-2 text-sm text-zinc-400">
                <Loader2 size={14} className="animate-spin" />
                Loading role assignments...
              </div>
            ) : roleAssignments.length === 0 ? (
              <p className="text-sm text-zinc-500 italic">
                No roles assigned. Click "Add Role" to assign a role to this user.
              </p>
            ) : (
              <div className="space-y-2">
                {roleAssignments.map((assignment, index) => (
                  <RoleAssignmentRow
                    key={index}
                    assignment={assignment}
                    index={index}
                    roles={roles}
                    groups={groups}
                    onChange={updateRoleAssignment}
                    onRemove={removeRoleAssignment}
                    canRemove={isEditing || roleAssignments.length > 1}
                    readOnly={isSelf}
                  />
                ))}
              </div>
            )}
            {roleError && <p className="mt-1 text-sm text-red-400">{roleError}</p>}

            {(() => {
              const effective = buildEffectivePermissionsSummary(roleAssignments, roles, groups);
              if (effective.length === 0) return null;
              return (
                <div className="mt-3 p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <Info size={14} className="text-blue-300" />
                    <span className="text-xs font-semibold uppercase tracking-wide text-blue-200">
                      What this user will be able to do
                    </span>
                  </div>
                  <ul className="space-y-2 text-sm text-zinc-300">
                    {effective.map(({ role, group }, i) => (
                      <li key={`${role.id}-${group.id}-${i}`} className="leading-relaxed">
                        <span className="text-white font-medium">As {role.displayName}</span> in{" "}
                        <span className="text-white font-medium">{group.name}</span>
                        <span className="text-zinc-500"> ({LABEL_CONFIG[group.label].displayName})</span>
                        {role.description && (
                          <span className="block text-zinc-400 text-xs mt-0.5">{role.description}</span>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })()}
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={handleClose}
              disabled={isSubmitting}
              className="px-4 py-2 text-sm font-medium text-zinc-300 hover:text-white bg-zinc-800 hover:bg-zinc-700 rounded transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-500 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? "Saving..." : isEditing ? "Save Changes" : "Create User"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
